// src/components/UserGallery/EditableUserRow.js
import React, { useState } from 'react';

const API_DOMAIN = "https://test-api.stainedglassusa.com";

export default function EditableUserRow({ user, token, onUpdateSuccess }) {
  // `user` is the original user object passed as a prop
  // `token` is the admin's authentication token
  // `onUpdateSuccess` is a callback function to update the parent component's state

  const [isEditing, setIsEditing] = useState(false); // State to toggle edit mode
  const [formData, setFormData] = useState({
    // Initialize form data with current user details, excluding username and id
    email: user.email || '',
    phone_number: user.phone_number || '',
    shipping_address: user.shipping_address || '',
    shipping_zip_code: user.shipping_zip_code || '',
    state: user.state || '',
    city: user.city || '',
    street: user.street || '',
    email_validated: user.email_validated || false, // Boolean checkbox
    admin: user.admin || false // Boolean checkbox
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Handle changes in input fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null); // Clear errors on change
    setSuccess(false); // Clear success message on change
  };

  // Toggle edit mode
  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(false);
  };

  // Handle saving changes to the backend
  const handleSaveClick = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Send PATCH request to update the user
      const response = await fetch(`${API_DOMAIN}/users/${user.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors ? errorData.errors.join(', ') : errorData.error || `HTTP error! Status: ${response.status}`);
      }

      const updatedUserResponse = await response.json();
      console.log("User update response:", updatedUserResponse);

      // Ensure the structure matches what we expect (e.g., { user: { ... } })
      const updatedUser = updatedUserResponse.user || updatedUserResponse; // Adjust based on your API response structure

      // Call the callback to update the parent component's state
      onUpdateSuccess(updatedUser);
      setIsEditing(false); // Exit edit mode
      setSuccess(true);
      // Re-initialize formData with the truly updated values from the server
      setFormData({
        email: updatedUser.email || '',
        phone_number: updatedUser.phone_number || '',
        shipping_address: updatedUser.shipping_address || '',
        shipping_zip_code: updatedUser.shipping_zip_code || '',
        state: updatedUser.state || '',
        city: updatedUser.city || '',
        street: updatedUser.street || '',
        email_validated: updatedUser.email_validated || false,
        admin: updatedUser.admin || false
      });

    } catch (err) {
      console.error("Error updating user:", err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Basic styling for table cells for readability
  const cellStyle = { padding: '8px', border: '1px solid #eee' };
  const inputStyle = { width: '100%', padding: '5px', boxSizing: 'border-box' };
  const buttonStyle = { padding: '8px 12px', cursor: 'pointer', margin: '0 5px', borderRadius: '5px', border: 'none' };

  return (
    <tr style={{ backgroundColor: success ? '#d4edda' : (error ? '#f8d7da' : 'white') }}>
      <td style={cellStyle}>{user.id}</td>
      <td style={cellStyle}>
        {/* Username is displayed but not editable */}
        {user.username}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.email
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="text"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.phone_number
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="text"
            name="shipping_address"
            value={formData.shipping_address}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.shipping_address
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="text"
            name="shipping_zip_code"
            value={formData.shipping_zip_code}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.shipping_zip_code
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.state
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.city
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <input
            type="text"
            name="street"
            value={formData.street}
            onChange={handleChange}
            style={inputStyle}
          />
        ) : (
          user.street
        )}
      </td>
      <td style={cellStyle} align="center">
        {isEditing ? (
          <input
            type="checkbox"
            name="email_validated"
            checked={formData.email_validated}
            onChange={handleChange}
          />
        ) : (
          user.email_validated ? 'Yes' : 'No'
        )}
      </td>
      <td style={cellStyle} align="center">
        {isEditing ? (
          <input
            type="checkbox"
            name="admin"
            checked={formData.admin}
            onChange={handleChange}
          />
        ) : (
          user.admin ? 'Yes' : 'No'
        )}
      </td>
      <td style={cellStyle}>
        {isEditing ? (
          <>
            <button
              onClick={handleSaveClick}
              disabled={loading}
              style={{ ...buttonStyle, backgroundColor: '#28a745', color: 'white' }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setError(null);
                setSuccess(false);
                // Reset form data to original user data if cancelling edit
                setFormData({
                  email: user.email || '',
                  phone_number: user.phone_number || '',
                  shipping_address: user.shipping_address || '',
                  shipping_zip_code: user.shipping_zip_code || '',
                  state: user.state || '',
                  city: user.city || '',
                  street: user.street || '',
                  email_validated: user.email_validated || false,
                  admin: user.admin || false
                });
              }}
              disabled={loading}
              style={{ ...buttonStyle, backgroundColor: '#dc3545', color: 'white' }}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={handleEditClick}
            style={{ ...buttonStyle, backgroundColor: '#007bff', color: 'white' }}
          >
            Edit
          </button>
        )}
        {error && <div style={{ color: 'red', fontSize: '0.8em', marginTop: '5px' }}>{error}</div>}
        {success && <div style={{ color: 'green', fontSize: '0.8em', marginTop: '5px' }}>Saved!</div>}
      </td>
    </tr>
  );
}